import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Button,
  Drawer,
  Space,
  Tooltip,
  message,
} from "antd"
import { CopyOutlined, LinkOutlined, ReloadOutlined } from "@ant-design/icons"
import apiClient from "../../../Utils/apiClient"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import StatusTag from "../../../components/StatusTag/StatusTag"
import HostnameLink from "../../../components/HostnameLink/HostnameLink"
import DeviceModelImage from "../../../components/DeviceModelImage/DeviceModelImage"
import RemoteAccessPanel from "../../../components/RemoteAccess/RemoteAccessPanel"
import AutoCompleteBox from "../../../components/AutoCompleteBox/AutoCompleteBox"
import {
  getStoredPageSize,
  storePageSize,
} from "../../../Utils/TableUtils/TableUtils"
import {
  copyText,
  hasRustdeskId,
  launchRustdesk,
  osLabel,
  sessionStatusOf,
} from "../../../Utils/remoteSupport"
import { renderDate } from "../../../Utils/utils"
import { resolveDeviceModelImage } from "../../../Utils/deviceModelImage"
import { useSelector } from "react-redux"
import "../../Operations/Operations.scss"
import "./RemoteSupport.scss"

const shortUser = (value) => {
  const text = String(value || "").trim()
  if (!text) return "Unassigned"
  const parts = text.split(/\\|\//)
  return parts[parts.length - 1] || text
}

const VIEW_CARDS = [
  { key: "all", label: "In this page", tone: "#2563eb", soft: "rgba(59, 130, 246, 0.14)" },
  { key: "ready", label: "Ready for remote", tone: "#059669", soft: "rgba(34, 197, 94, 0.14)" },
  { key: "connected", label: "Connected now", tone: "#0891b2", soft: "rgba(56, 189, 248, 0.14)" },
  { key: "missing", label: "Missing RustDesk ID", tone: "#64748b", soft: "rgba(148, 163, 184, 0.12)" },
]

const RemoteSupport = () => {
  const page = "Remote Support"
  const userInfo = useSelector((state) => state.user.data)
  const isAdmin = userInfo?.role === "network-admin"
  const [loading, setLoading] = useState(false)
  const [devices, setDevices] = useState([])
  const [view, setView] = useState("all")
  const [statusFilter, setStatusFilter] = useState("")
  const [selected, setSelected] = useState(null)
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })
  const [filters, setFilters] = useState({
    hostname: "",
    user: "",
  })
  const [sessions, setSessions] = useState([])

  const getDevices = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize,
    customFilters = filters,
    customStatus = statusFilter
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        ...customFilters,
      }
      if (customStatus) params.deviceStatus = customStatus
      const data = await apiClient("devices", "GET", null, { params })
      if (data?.data) {
        setDevices(data.data || [])
      }
      try {
        const audit = await apiClient("devices/remote-sessions")
        setSessions(audit?.data || [])
      } catch {
        setSessions([])
      }
      setPaginationData(data.pageData || paginationData)
    } catch {
      message.error("Could not load devices for remote support")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getDevices()
    const timer = setInterval(() => getDevices(), 20000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const visibleDevices = useMemo(() => {
    return devices.filter((device) => {
      if (view === "ready") return hasRustdeskId(device)
      if (view === "connected") return sessionStatusOf(device) === "connected"
      if (view === "missing") return !hasRustdeskId(device)
      return true
    })
  }, [devices, view])

  const summary = useMemo(() => {
    const ready = devices.filter(hasRustdeskId)
    return {
      all: devices.length,
      ready: ready.length,
      connected: devices.filter((device) => sessionStatusOf(device) === "connected")
        .length,
      missing: devices.length - ready.length,
    }
  }, [devices])

  const handleClearFilter = () => {
    const empty = { hostname: "", user: "" }
    setFilters(empty)
    setStatusFilter("")
    getDevices(1, paginationData.pageSize, empty, "")
  }

  const columns = [
    {
      title: "Device",
      dataIndex: "hostname",
      key: "hostname",
      width: 260,
      render: (_, record) => {
        const model = resolveDeviceModelImage(record)
        return (
          <div className="inv-device">
            <DeviceModelImage device={record} size="sm" />
            <div className="inv-device__copy">
              <div className="inv-device__title">
                <span
                  className={`inv-device__pulse inv-device__pulse--${String(
                    record.deviceStatus || "offline"
                  )
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  aria-hidden
                />
                <HostnameLink record={record} isAdmin={isAdmin}>
                  {record.hostname}
                </HostnameLink>
              </div>
              <div className="inv-device__meta" title={record.user || ""}>
                <span>{shortUser(record.user)}</span>
                <span className="inv-device__dot" />
                <span>{osLabel(record)}</span>
                <span className="inv-device__dot" />
                <span title={model.displayModel}>{model.label}</span>
              </div>
            </div>
          </div>
        )
      },
    },
    {
      title: "Presence",
      dataIndex: "deviceStatus",
      key: "deviceStatus",
      width: 108,
      render: (val) => <StatusTag status={val} />,
    },
    {
      title: "Session",
      key: "session",
      width: 120,
      render: (_, record) => <StatusTag status={sessionStatusOf(record)} />,
    },
    {
      title: "Credentials",
      key: "credentials",
      width: 200,
      render: (_, record) =>
        record.rustdesk_id ? (
          <div className="remote-creds">
            <button
              type="button"
              className="remote-creds__row"
              onClick={() => copyText(record.rustdesk_id, "RustDesk ID copied")}
            >
              <span className="remote-support__mono">{record.rustdesk_id}</span>
              <CopyOutlined />
            </button>
            {record.rustdesk_password ? (
              <button
                type="button"
                className="remote-creds__row remote-creds__row--secret"
                onClick={() =>
                  copyText(record.rustdesk_password, "Password copied")
                }
              >
                <span className="remote-support__secret">••••••••</span>
                <CopyOutlined />
              </button>
            ) : null}
          </div>
        ) : (
          <span className="ops-muted">No ID yet</span>
        ),
    },
    {
      title: "Last connected",
      dataIndex: "lastRemoteConnectAt",
      key: "lastRemoteConnectAt",
      width: 140,
      render: (val) => renderDate(val) || "—",
    },
    {
      title: "",
      key: "actions",
      width: 180,
      fixed: "right",
      render: (_, record) => (
        <Space size={8}>
          <Button size="small" type="primary" onClick={() => setSelected(record)}>
            Open
          </Button>
          <Tooltip title="Open in RustDesk">
            <Button
              size="small"
              icon={<LinkOutlined />}
              disabled={!hasRustdeskId(record)}
              onClick={() => launchRustdesk(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  const auditColumns = [
    {
      title: "Device",
      dataIndex: "hostname",
      render: (value) => <span className="ops-name">{value || "—"}</span>,
    },
    { title: "Actor", dataIndex: "actor", render: (value) => value || "—" },
    {
      title: "Action",
      dataIndex: "action",
      render: (value) => <StatusTag status={value || "—"} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value) => <StatusTag status={value || "—"} />,
    },
    {
      title: "Started",
      dataIndex: "startedAt",
      render: (value) => renderDate(value) || "—",
    },
    {
      title: "Ended",
      dataIndex: "endedAt",
      render: (value) => renderDate(value) || "—",
    },
    {
      title: "Duration",
      dataIndex: "durationMs",
      render: (value) =>
        value ? (
          <span className="inv-mono">{Math.round(Number(value) / 60000)} min</span>
        ) : (
          "—"
        ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      ellipsis: true,
      render: (value) => value || "—",
    },
  ]

  return (
    <PanelLayout
      title="Remote support"
      subtitle="Start RustDesk on an endpoint, copy credentials, and join from this computer."
      actions={
        <Button icon={<ReloadOutlined />} onClick={() => getDevices()} loading={loading}>
          Refresh
        </Button>
      }
    >
      <div className="remote-support">
        <Alert
          className="mb-3 remote-support__banner"
          type="info"
          showIcon
          message="RustDesk remote access"
          description="Start access on the endpoint, copy the ID and password, then join from the RustDesk app on this computer. End the session when support is finished."
        />

        <div className="ops-kpis">
          {VIEW_CARDS.map((card) => (
            <button
              type="button"
              key={card.key}
              className={`ops-kpi${view === card.key ? " is-active" : ""}`}
              style={{ "--act-tone": card.tone, "--act-soft": card.soft }}
              onClick={() => setView(card.key)}
            >
              <div>
                <span className="ops-kpi-label">{card.label}</span>
                <strong>{summary[card.key] ?? 0}</strong>
              </div>
            </button>
          ))}
        </div>

        <div className="ops-toolbar remote-support__toolbar">
          <span className="ops-toolbar__count">
            {visibleDevices.length} device{visibleDevices.length === 1 ? "" : "s"}
            {view !== "all" ? ` · ${VIEW_CARDS.find((c) => c.key === view)?.label}` : ""}
          </span>
          <div className="dashboard-filter-chips">
            <span>Presence</span>
            {["", "Online", "Away", "Offline"].map((value) => (
              <Button
                key={value || "all-status"}
                size="small"
                type={statusFilter === value ? "primary" : "default"}
                onClick={() => {
                  setStatusFilter(value)
                  getDevices(1, paginationData.pageSize, filters, value)
                }}
              >
                {value || "All"}
              </Button>
            ))}
          </div>
        </div>

        <TableBox
          page={page}
          title="Endpoints"
          columns={columns}
          data={visibleDevices}
          loading={loading}
          className="dashboard-inventory-table"
          scroll={{ x: 1180 }}
          pagination={{
            total: paginationData.total,
            current: paginationData.page,
            pageSize: paginationData.pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30", "50"],
            onChange: (pageNum, pageSize) => {
              setPaginationData((prev) => ({ ...prev, page: pageNum, pageSize }))
              storePageSize(page, pageSize)
              getDevices(pageNum, pageSize)
            },
          }}
          enableFilter
          filtercontent={
            <div className="p-3">
              <AutoCompleteBox
                label="Hostname"
                name="hostname"
                className="mb-3 w-100"
                endPoint="users"
                placeholder="Search hostname"
                value={filters.hostname}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, hostname: value }))
                }
              />
              <AutoCompleteBox
                label="User name"
                name="user"
                className="mb-3 w-100"
                endPoint="users"
                placeholder="Search user"
                value={filters.user}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, user: value }))
                }
              />
            </div>
          }
          onFilter={() => {
            setPaginationData((prev) => ({ ...prev, page: 1 }))
            getDevices(1, paginationData.pageSize, filters)
          }}
          onClear={handleClearFilter}
        />

        <div className="remote-support__audit">
          <div className="ops-toolbar">
            <span className="ops-toolbar__count">
              Session audit · {sessions.length} recent
            </span>
          </div>
          <TableBox
            title={null}
            columns={auditColumns}
            data={sessions}
            pagination={false}
            className="dashboard-inventory-table"
          />
        </div>
      </div>

      <Drawer
        title={selected ? `Remote access · ${selected.hostname}` : "Remote access"}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        width={520}
        destroyOnClose
      >
        {selected ? (
          <RemoteAccessPanel
            device={devices.find((item) => item._id === selected._id) || selected}
            onRefresh={getDevices}
          />
        ) : null}
      </Drawer>
    </PanelLayout>
  )
}

export default RemoteSupport
